import posthog from "posthog-js";
import * as Sentry from "@sentry/react";

export function initAnalytics() {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com",
    person_profiles: "never",       // fully anonymous — no user profiles created
    capture_pageview: false,        // no pageviews — disables web analytics
    capture_pageleave: false,
    disable_session_recording: true,
    autocapture: false,
    capture_heatmaps: false,
    disable_web_experiments: true,
    ip: false,
    persistence: "memory",
    before_send: (event) => {
      // Drop any web analytics events that PostHog may inject internally
      if (event?.event?.startsWith("$") && event.event !== "$set") return null;
      return event;
    },
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug();
    },
  });
}

// ─── Errors ──────────────────────────────────────────────────────────────────

export function logError(err: unknown, context?: Record<string, string>) {
  Sentry.captureException(err, context ? { extra: context } : undefined);
}

// ─── Events ──────────────────────────────────────────────────────────────────

export function trackWalletConnected(props: { connector: string }) {
  posthog.capture("wallet_connected", props);
}

export function trackWalletDisconnected() {
  posthog.capture("wallet_disconnected");
}

export function trackTransferInitiated(props: { asset: string; network: string }) {
  posthog.capture("transfer_initiated", props);
}

export function trackTransferSubmitted(props: { asset: string; network: string }) {
  posthog.capture("transfer_submitted", props);
}

export function trackTransferCompleted(props: { asset: string; network: string }) {
  posthog.capture("transfer_completed", props);
}

export function trackTransferFailed(props: { asset: string; network: string; reason: string }) {
  posthog.capture("transfer_failed", props);
}

export function trackTransferAbandoned(props: { asset: string; network: string; step: "configure" | "review" }) {
  posthog.capture("transfer_abandoned", props);
}

export function trackAssetChanged(props: { asset: string }) {
  posthog.capture("asset_changed", props);
}

export function trackTransferReviewed(props: { asset: string; amount: string }) {
  posthog.capture("transfer_reviewed", props);
}

export function trackTransferResumed(props: { asset: string }) {
  posthog.capture("transfer_resumed", props);
}

export function trackCustomDestinationToggled(props: { mode: "custom" | "own_wallet" }) {
  posthog.capture("custom_destination_toggled", props);
}
